/**
 * Lettered paren-list tokenizer for Cmarkdown.
 *
 * Markdown only recognises `1)` / `1.` as ordered-list markers, so a run of
 * `a) first` / `b) second` lines lexes as one paragraph with soft line breaks.
 * Models write that form constantly, so this block extension picks the run up
 * and emits a real list token; the digit form (`1)`) keeps flowing through
 * marked's native ordered-list tokenizer untouched.
 *
 * A run only matches when the markers are *sequential* single letters in one
 * case (`a,b,c…` or `A,B,C…`) across at least two consecutive lines, which is
 * what keeps prose like "…and b) is wrong" from turning into a list.
 */

import type { TokenizerExtension } from 'marked';
import {
  PAREN_LIST_TOKEN_NAME,
  type ParenListItem,
  type ParenListToken,
} from './paren-list-types';

const MARKED_BLOCK_TOKEN_LEVEL = 'block';
/** `a) body` — optional indent, one letter, `)`, at least one space, a body. */
const PAREN_LIST_ITEM_PATTERN = /^[ \t]{0,3}([A-Za-z])\)[ \t]+(\S.*)$/;
/** Cheap scan for a line that could open a run (used by marked's `start`). */
const PAREN_LIST_START_PATTERN = /^[ \t]{0,3}[A-Za-z]\)[ \t]+\S/m;
const MIN_PAREN_LIST_ITEMS = 2;
const LINE_SEPARATOR = '\n';

interface ParsedParenLine {
  marker: string;
  text: string;
}

function parseParenLine(line: string): ParsedParenLine | undefined {
  const match = PAREN_LIST_ITEM_PATTERN.exec(line);
  if (!match) {
    return undefined;
  }

  return { marker: match[1], text: match[2].trim() };
}

function isNextMarkerInSequence({
  previous,
  next,
}: {
  previous: string;
  next: string;
}): boolean {
  const isPreviousUpper = previous === previous.toUpperCase();
  const isNextUpper = next === next.toUpperCase();
  if (isPreviousUpper !== isNextUpper) {
    return false;
  }

  return next.charCodeAt(0) === previous.charCodeAt(0) + 1;
}

function collectParenListItems(lines: string[]): ParenListItem[] {
  const items: ParenListItem[] = [];

  for (const line of lines) {
    const parsed = parseParenLine(line);
    if (!parsed) {
      break;
    }

    const previous = items[items.length - 1];
    if (
      previous &&
      !isNextMarkerInSequence({ previous: previous.marker, next: parsed.marker })
    ) {
      break;
    }

    items.push({ marker: parsed.marker, text: parsed.text });
  }

  return items;
}

export function createParenListTokenizerExtension(): TokenizerExtension {
  return {
    name: PAREN_LIST_TOKEN_NAME,
    level: MARKED_BLOCK_TOKEN_LEVEL,
    start(src) {
      const match = PAREN_LIST_START_PATTERN.exec(src);
      return match?.index;
    },
    tokenizer(src) {
      const items = collectParenListItems(src.split(LINE_SEPARATOR));
      if (items.length < MIN_PAREN_LIST_ITEMS) {
        return undefined;
      }

      const consumedLines = src.split(LINE_SEPARATOR).slice(0, items.length);
      const token: ParenListToken = {
        type: PAREN_LIST_TOKEN_NAME,
        raw: consumedLines.join(LINE_SEPARATOR),
        items,
      };

      return token;
    },
  };
}
